import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { reloadIfCurrentTenant } from "../utils";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { IoArrowForwardSharp } from "react-icons/io5";
import { IoMdArrowBack } from "react-icons/io";
import client from "../api/client";
import { BRAND_COLOR_FIELDS, FONT_COLOR_FIELDS, ColorField, SectionCard } from "./ThemeColorFields";
import { DEFAULT_THEME, resolveLogoUrl } from "../Hooks/useTenantTheme";
import LogoUploader from "./LogoUploader";
import DispositionColorsSection from "./DispositionColorsSection";
import ButtonLoader from "./ButtonLoader";

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const SUBDOMAIN_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const EMPTY_FORM = {
  name: "",
  slug: "",
  subdomain: "",
  hubspot_tenant_property_value: "",
  household_object_id: "",
  task_object_id: "",
  theme: { ...DEFAULT_THEME },
};

const TENANT_VALIDATION_SCHEMA = yup.object().shape({
  name: yup.string().trim().required("Tenant name is required."),
  slug: yup
    .string()
    .trim()
    .required("Slug is required.")
    .matches(SLUG_PATTERN, "Use lowercase letters, numbers, and hyphens only."),
  subdomain: yup
    .string()
    .trim()
    .required("Subdomain is required.")
    .matches(SUBDOMAIN_PATTERN, "Use lowercase letters, numbers, and hyphens only."),
  hubspot_tenant_property_value: yup.string().trim(),
  household_object_id: yup.string().trim(),
  task_object_id: yup.string().trim(),
});

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const FieldLabel = ({ children, required }) => (
  <h6 className="text-canvas-directoryText">
    {children}
    {required && <span className="text-canvas-danger"> *</span>}
  </h6>
);

const TextField = ({ name, label, required, placeholder, onValueChange }) => (
  <div className="flex flex-col gap-1.5">
    <FieldLabel required={required}>{label}</FieldLabel>
    <Field name={name}>
      {({ field, form }) => (
        <input
          {...field}
          placeholder={placeholder}
          className="border border-canvas-tersery rounded p-3 focus:outline-none focus:border-canvas-primary"
          onChange={(e) => {
            field.onChange(e);
            if (onValueChange) onValueChange(e.target.value, form);
          }}
        />
      )}
    </Field>
    <ErrorMessage name={name} component="span" className="text-canvas-danger text-xs" />
  </div>
);

// Full-page create/edit form (was previously a modal on TenantManagement.js) —
// gives the HubSpot config + branding sections room to breathe instead of
// scrolling inside a fixed-height overlay.
const TenantForm = () => {
  const { id } = useParams();
  const editingId = id || null;
  const navigate = useNavigate();

  const [initialValues, setInitialValues] = useState(EMPTY_FORM);
  const [slugTouchedManually, setSlugTouchedManually] = useState(Boolean(editingId));
  const [subdomainTouchedManually, setSubdomainTouchedManually] = useState(Boolean(editingId));
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isLoadingTenant, setIsLoadingTenant] = useState(Boolean(editingId));

  useEffect(() => {
    if (!editingId) return;

    let isMounted = true;
    setIsLoadingTenant(true);
    client
      .get(`/tenants/${editingId}`)
      .then((res) => {
        if (!isMounted) return;
        const tenant = res.data;
        setLogoPreview(resolveLogoUrl(tenant.theme?.logoUrl));
        setInitialValues({
          name: tenant.name || "",
          slug: tenant.slug || "",
          subdomain: tenant.subdomain || "",
          hubspot_tenant_property_value: tenant.hubspot_tenant_property_value || "",
          household_object_id: tenant.household_object_id || "",
          task_object_id: tenant.task_object_id || "",
          theme: { ...DEFAULT_THEME, ...(tenant.theme || {}) },
        });
      })
      .catch((error) => {
        toast.error(error.response?.data?.message || "Failed to load tenant.");
      })
      .finally(() => {
        if (isMounted) setIsLoadingTenant(false);
      });

    return () => {
      isMounted = false;
    };
  }, [editingId]);

  const handleLogoSelect = (file) => {
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleLogoRemove = (setFieldValue) => {
    setLogoFile(null);
    setLogoPreview(null);
    setFieldValue("theme.logoUrl", null);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let logoUrl = values.theme.logoUrl;

      if (logoFile) {
        setIsUploadingLogo(true);
        const formData = new FormData();
        formData.append("logo", logoFile);
        const uploadRes = await client.post("/tenants/logo", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        logoUrl = uploadRes.data.logoUrl;
        setIsUploadingLogo(false);
      }

      const payload = { ...values, theme: { ...values.theme, logoUrl } };

      if (editingId) {
        await client.put(`/tenants/${editingId}`, payload);
        toast.success("Tenant updated.");
        navigate("/tenant-management");
        reloadIfCurrentTenant(editingId);
      } else {
        await client.post("/tenants", payload);
        toast.success("Tenant created.");
        navigate("/tenant-management");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save tenant.");
    } finally {
      setSubmitting(false);
      setIsUploadingLogo(false);
    }
  };

  return (
    <div className="bg-canvas-userbar w-full px-6 py-6">
      <div className="bg-canvas-white rounded">
        <div className="flex items-center gap-4 p-6 border-b border-canvas-tersery/30">
          <button
            type="button"
            onClick={() => navigate("/tenant-management")}
            aria-label="Back to Tenant Management"
            className="text-canvas-secondery hover:text-canvas-primary transition-colors"
          >
            <IoMdArrowBack className="w-6 h-6" />
          </button>
          <div className="flex flex-col gap-1">
            <h2>{editingId ? "Edit Tenant" : "New Tenant"}</h2>
            <h6 className="text-canvas-directoryText font-normal">
              {editingId
                ? "Update this tenant's configuration and branding."
                : "Set up a new tenant workspace with its own HubSpot data and branding."}
            </h6>
          </div>
        </div>

        {isLoadingTenant ? (
          <div className="py-4 md:px-8 px-5 w-full animate-pulse">
            {[1, 2, 3].map((section) => (
              <div key={section} className="mb-6">
                <div className="h-4 w-48 rounded bg-gray-200 dark:bg-gray-700 mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4">
                  {[1, 2, 3].map((field) => (
                    <div key={field} className="h-11 rounded bg-gray-200 dark:bg-gray-700" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-4 md:px-8 px-5 w-full">
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={TENANT_VALIDATION_SCHEMA}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue, isSubmitting }) => (
                <Form>
                  <SectionCard
                    title="1. Tenant Details"
                    description="The name and URLs used to identify this tenant across the platform."
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4">
                      <TextField
                        name="name"
                        label="Tenant Name"
                        required
                        placeholder="e.g. Acme Canvassing"
                        onValueChange={(value) => {
                          if (!slugTouchedManually) setFieldValue("slug", slugify(value));
                          if (!subdomainTouchedManually) setFieldValue("subdomain", slugify(value));
                        }}
                      />
                      <TextField
                        name="slug"
                        label="Slug"
                        required
                        placeholder="Lowercase letters, numbers, and hyphens only. Used in internal URLs."
                        onValueChange={() => setSlugTouchedManually(true)}
                      />
                      <TextField
                        name="subdomain"
                        label="Subdomain"
                        required
                        placeholder="Where this tenant is accessed, e.g. acme.yourapp.com."
                        onValueChange={() => setSubdomainTouchedManually(true)}
                      />
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="2. HubSpot Configuration"
                    description="Optional. Mirrors the invite flow's HubSpot portal mapping fields — leave blank to configure later."
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4">
                      <TextField
                        name="hubspot_tenant_property_value"
                        label="Tenant Tag Value"
                        placeholder="Value stored in the tenant_id_hs HubSpot property."
                      />
                      <TextField
                        name="household_object_id"
                        label="Household Object ID"
                        placeholder="HubSpot custom object ID for households."
                      />
                      <TextField
                        name="task_object_id"
                        label="Task Object ID"
                        placeholder="HubSpot custom object ID for tasks."
                      />
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="3. Brand Identity"
                    description="The name and logo shown to this tenant's users throughout the app."
                  >
                    <div className="flex flex-wrap items-start gap-4">
                      <div className="flex flex-col gap-2 order-2 sm:order-1 min-w-[260px]">
                        <FieldLabel>App Name</FieldLabel>
                        <Field name="theme.appName">
                          {({ field }) => (
                            <input
                              {...field}
                              placeholder="Shown in the browser tab, sidebar, and login page for this tenant."
                              className="border border-canvas-tersery rounded p-3 w-full max-w-[480px] focus:outline-none focus:border-canvas-primary"
                            />
                          )}
                        </Field>
                      </div>

                      <div className="flex flex-col gap-2 order-1 sm:order-2">
                        <FieldLabel>Logo</FieldLabel>
                        <LogoUploader
                          preview={logoPreview}
                          fileName={logoFile?.name}
                          onSelect={handleLogoSelect}
                          onRemove={() => handleLogoRemove(setFieldValue)}
                        />
                      </div>
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="4. Brand Colors"
                    description="Applied to the sidebar, buttons, and highlights for this tenant. Reuses the same picker as the self-service Theme Settings page."
                  >
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-4">
                      {(() => {
                        const fields = [...BRAND_COLOR_FIELDS, ...FONT_COLOR_FIELDS];
                        return fields.map(({ key, label }, index) => (
                          <ColorField
                            key={key}
                            label={label}
                            value={values.theme[key]}
                            onChange={(value) => setFieldValue(`theme.${key}`, value)}
                            align={index === fields.length - 1 ? "right" : "left"}
                          />
                        ));
                      })()}
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        className="font-outfit text-[12px] uppercase font-bold border border-canvas-primary text-canvas-primary py-2.5 px-7 rounded-md hover:bg-canvas-primary/5 transition-colors"
                        onClick={() => {
                          setLogoFile(null);
                          setLogoPreview(null);
                          setFieldValue("theme", {
                            ...DEFAULT_THEME,
                            dispositionColors: values.theme.dispositionColors,
                          });
                        }}
                      >
                        Reset to Default
                      </button>
                    </div>
                  </SectionCard>

                  <DispositionColorsSection
                    title="5. Disposition Colors"
                    tenantId={editingId}
                    colors={values.theme.dispositionColors}
                    onChange={(next) => setFieldValue("theme.dispositionColors", next)}
                  />

                  <div className="flex items-center gap-4 mt-6 pb-6 px-5">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-canvas-primary rounded flex justify-center items-center px-8 font-outfit font-semibold text-canvas-white gap-2 uppercase py-3 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSubmitting && <ButtonLoader />}
                      {isSubmitting
                        ? isUploadingLogo
                          ? "Uploading logo..."
                          : "Saving..."
                        : editingId
                        ? "Save Changes"
                        : "Create Tenant"}{" "}
                      {!isSubmitting && <IoArrowForwardSharp />}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate("/tenant-management")}
                      className="font-outfit text-[12px] uppercase font-bold border border-canvas-primary text-canvas-primary py-3 px-6 rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantForm;
